import React, { useState } from 'react';

const isDirectVideoFile = (url) => /\.(mp4|webm|mov)(\?|$)/i.test(url || '');
const isOfficeDoc = (url) =>
  /\.(docx?|pptx?|xlsx?)(\?|$)/i.test(url || '');
const isBlockedHost = (url) => {
  // SharePoint Business / Microsoft 365 hosts that send frame-ancestors blocking us
  return /sharepoint\.com|crmportals-my\.sharepoint|onedrive\.live\.com\/.*nav=/i.test(url || '');
};

const wrapOfficeViewer = (url) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

// ─── YouTube helpers ───────────────────────────────────────────────────────
//
// Pre-emptive defence against the dreaded **YouTube error 150 / 153**
// ("video owner has disabled playback on other websites"). The trick:
// route everything through `youtube-nocookie.com/embed/...` which has
// permissive embed rules in many cases where `youtube.com/embed/...`
// refuses. We also enable the IFrame Player API so we can listen for
// onError events and swap to a fallback if even nocookie is blocked.
//
// Returns either:
//   { kind: 'youtube', videoId, embedSrc, watchUrl, thumbUrl }  or
//   null  (not a YouTube URL)
const parseYouTube = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.href);
    const host = u.hostname.replace(/^www\./, '');
    let videoId = null;

    if (host === 'youtu.be') {
      videoId = u.pathname.replace(/^\//, '').split('/')[0] || null;
    } else if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (u.pathname.startsWith('/watch')) {
        videoId = u.searchParams.get('v');
      } else if (u.pathname.startsWith('/embed/')) {
        videoId = u.pathname.split('/')[2] || null;
      } else if (u.pathname.startsWith('/shorts/')) {
        videoId = u.pathname.split('/')[2] || null;
      }
    }
    if (!videoId) return null;

    // Carry through start-time parameter if present.
    const start = u.searchParams.get('t') || u.searchParams.get('start');
    const startNum = start ? parseInt(String(start).replace(/[^\d]/g, ''), 10) : 0;
    const params = new URLSearchParams({
      enablejsapi: '1',                                     // for postMessage error events
      rel: '0',                                             // no related-video clutter
      modestbranding: '1',
      origin: typeof window !== 'undefined' ? window.location.origin : '',
    });
    if (startNum) params.set('start', String(startNum));

    return {
      kind: 'youtube',
      videoId,
      embedSrc: `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`,
      // Watch URL with `rel=0` suppresses the post-video "More videos"
      // grid that students would otherwise see at the end. We also pin
      // `feature=emb_imp_woyt` which YouTube treats as an embed-implicit
      // referral — the watch page renders in a calmer layout in some
      // browser configurations.
      watchUrl:
        `https://www.youtube.com/watch?v=${videoId}` +
        `&rel=0&modestbranding=1${startNum ? `&t=${startNum}s` : ''}`,
      thumbUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    };
  } catch {
    return null;
  }
};

// (YouTube IFrame Player API onError detection deliberately removed —
// see the comment above the YouTube branch in the render body. Inline
// embedding of third-party YouTube content is no longer attempted, so
// the listener is unused.)

/**
 * EmbedFrame
 * Always renders inline with full-bleed iframe/video/object. Small floating
 * toolbar (top-right) offers Open / Download.
 *
 * YouTube videos: ALWAYS render a thumbnail + "▶ Watch on YouTube" CTA
 * (no iframe attempt). This is the only 100%-reliable behaviour because
 * owner-restricted videos (error 150/153) render YouTube's own
 * "Video unavailable" screen inside the iframe without firing any
 * postMessage event we can listen for.
 *
 * For inline in-app playback (no embed permission needed at all),
 * faculty should use "Upload MP4" on the Topic editor — self-hosted
 * files play via the <video> branch below.
 */
const EmbedFrame = ({ kind, src, title, aspect = 'tall' }) => {
  const [errored, setErrored] = useState(false);     // generic load error

  if (!src) return null;
  const yt = kind === 'video' ? parseYouTube(src) : null;

  const containerStyle =
    aspect === 'video'
      ? { paddingBottom: '56.25%', position: 'relative', width: '100%' }
      : { height: '78vh', position: 'relative', width: '100%' };

  const innerClass =
    aspect === 'video'
      ? 'absolute inset-0 w-full h-full rounded-lg border border-gray-200'
      : 'w-full h-full rounded-lg border border-gray-200';

  const downloadName = (() => {
    try {
      const u = new URL(src);
      return u.pathname.split('/').pop() || 'file';
    } catch {
      return 'file';
    }
  })();

  // ─── YouTube ALWAYS renders the safe external-watch panel ────────────
  //
  // Why no embed attempt: YouTube's IFrame Player API only fires
  // `onError` events for SOME failure modes. For owner-restricted
  // videos (error 150/153) the player loads the iframe successfully,
  // then renders YouTube's OWN internal "Video unavailable" screen
  // without sending any postMessage event we can listen for. Result:
  // the iframe sits there showing YouTube's error UI, which is exactly
  // what we're trying to avoid showing students.
  //
  // The robust answer is to never embed third-party YouTube content.
  // Faculty wanting truly inline playback should use "Upload MP4" on
  // the Topic editor — self-hosted files play via the <video> branch
  // below with zero embed permissions required.
  if (yt) {
    return (
      <div className="w-full" data-testid="embedframe-video-fallback">
        <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
          <a href={yt.watchUrl} target="_blank" rel="noopener noreferrer"
             className="block relative group" data-testid="yt-fallback-thumb">
            <img src={yt.thumbUrl} alt={title || 'Video thumbnail'}
                 className="w-full h-auto block" loading="lazy" />
            <span
              className="absolute inset-0 flex items-center justify-center text-white font-bold
                         group-hover:scale-105 transition-transform"
              style={{ background: 'rgba(0,0,0,0.30)', fontSize: '5rem',
                       textShadow: '0 4px 16px rgba(0,0,0,0.7)' }}>
              ▶
            </span>
          </a>
          <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-3"
               style={{ background: 'rgba(252,211,89,0.10)' }}>
            <div className="text-xs text-gray-700">
              <b style={{ color: 'var(--gr-navy, #1A2B4C)' }}>
                Video opens on YouTube in a new tab.
              </b>
              <br />
              When you return, come back here to talk it through with the
              AI Tutor or take the quiz.
            </div>
            <a href={yt.watchUrl} target="_blank" rel="noopener noreferrer"
               className="btn-outline text-sm font-bold whitespace-nowrap"
               style={{ background: '#FCD359', color: '#1A2B4C', borderColor: '#E0B73D' }}
               data-testid="yt-fallback-watch-btn">
              ▶ Watch on YouTube ↗
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ─── Choose normal render mode ────────────────────────────────────────
  let mode = 'iframe';
  let renderSrc = src;
  if (yt) {
    mode = 'iframe';
    renderSrc = yt.embedSrc;
  } else if (kind === 'video' && isDirectVideoFile(src)) {
    mode = 'video';
  } else if (kind === 'pdf' && /\.pdf(\?|$)/i.test(src)) {
    mode = 'object';
  } else if (isOfficeDoc(src) && !isBlockedHost(src)) {
    mode = 'iframe';
    renderSrc = wrapOfficeViewer(src);
  }

  const showBlockedHint = !yt && isBlockedHost(src);

  return (
    <div className="w-full" data-testid={`embedframe-${kind}`}>
      {showBlockedHint && (
        <div className="mb-2 p-2 rounded text-xs flex items-center justify-between gap-2"
             style={{ background: '#FEF3C7', color: '#92400E' }}>
          <span>
            ⚠️ This source (SharePoint Business / OneDrive for Business) is known to refuse iframe embedding.
            Inline playback may not work. Best fix: re-upload the file using the Upload button in the Developer editor.
          </span>
        </div>
      )}

      <div style={containerStyle} className="bg-gray-50 rounded-lg overflow-hidden">
        {mode === 'video' && (
          <video
            src={src}
            controls
            className="w-full rounded-lg border border-gray-200 bg-black"
            style={{ maxHeight: '78vh' }}
            onError={() => setErrored(true)}
            data-testid="embed-video"
          >
            Your browser does not support HTML5 video.
          </video>
        )}

        {mode === 'object' && (
          <object data={src} type="application/pdf" className={innerClass} aria-label={title}>
            <iframe title={title} src={src} className={innerClass} />
          </object>
        )}

        {mode === 'iframe' && (
          <iframe
            title={title}
            src={renderSrc}
            className={innerClass}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid="embed-iframe"
          />
        )}

        {/* Floating toolbar */}
        <div className="absolute top-2 right-2 flex gap-1 z-10" data-testid="embed-toolbar">
          <a href={yt ? yt.watchUrl : src} target="_blank" rel="noreferrer"
             className="bg-white/95 hover:bg-white shadow rounded-md text-xs px-2 py-1 font-semibold"
             style={{ color: 'var(--unthsc-blue)' }}
             data-testid="embed-open-btn"
             title={yt ? 'Open on YouTube' : 'Open in new tab'}>
            {yt ? '▶ YouTube ↗' : 'Open ↗'}
          </a>
          {!yt && (
            <a href={src} download={downloadName}
               className="bg-white/95 hover:bg-white shadow rounded-md text-xs px-2 py-1 font-semibold"
               style={{ color: 'var(--unthsc-green-dark)' }}
               data-testid="embed-download-btn"
               title="Download">Download ⬇</a>
          )}
        </div>
      </div>

      {errored && (
        <div className="mt-2 text-xs text-red-700">
          The browser couldn&apos;t load this resource. Use the buttons above to open or download.
        </div>
      )}
    </div>
  );
};

export default EmbedFrame;
